// src/components/shared/MobileGate.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Device } from '@capacitor/device';
import DownloadApp from '../download/DownloadApp';

const ORIGIN = 'https://shepherdsindia.sbs';
const LATEST_URL = `${ORIGIN}/app/latest.json`;

function isMobileUA() {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
}
function isStandalonePWA() {
  if (typeof window === 'undefined') return false;
  const mm = window.matchMedia && window.matchMedia('(display-mode: standalone)');
  const isStandaloneMM = !!(mm && mm.matches);
  const isIOSStandalone = typeof navigator !== 'undefined' && (navigator.standalone === true);
  return isStandaloneMM || isIOSStandalone;
}
function isTWAReferrer() {
  if (typeof document === 'undefined') return false;
  return typeof document.referrer === 'string' && document.referrer.startsWith('android-app://');
}
function isCustomAppUA() {
  if (typeof navigator === 'undefined') return false;
  return /SBSApp\/\d+\.\d+/i.test(navigator.userAgent || '');
}
function isInAppViaQuery() {
  if (typeof window === 'undefined') return false;
  const search = new URLSearchParams(window.location.search);
  return search.get('inapp') === '1';
}

export default function MobileGate({ children }) {
  const [ready, setReady] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [latestMeta, setLatestMeta] = useState(null);

  const isMobile = useMemo(() => isMobileUA(), []);
  const isInApp = useMemo(() => {
    const w = typeof window !== 'undefined' ? window : {};
    const hasBridge = !!w.ShepNative;
    return isStandalonePWA() || isTWAReferrer() || isCustomAppUA() || isInAppViaQuery() || hasBridge;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Only bother on mobile; if not in app, we show Download flow anyway
        if (!isMobile) { setReady(true); return; }

        // Current installed app build (Capacitor)
        let appBuild = 0;
        try {
          const info = await Device.getInfo();
          appBuild = Number(info?.appBuild || 0);
        } catch {}

        // Fetch latest.json (no cache)
        let serverCode = 0, meta = null;
        try {
          const res = await fetch(LATEST_URL, { cache: 'no-store' });
          meta = await res.json();
          serverCode = Number(meta?.versionCode || 0);
        } catch {}

        if (!cancelled) {
          setLatestMeta(meta);
          // Update needed only if app is installed AND server > app
          setUpdateAvailable(isInApp && serverCode > appBuild);
          setReady(true);
        }
      } catch {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [isMobile, isInApp]);

  if (!ready) return null;

  // Mobile logic:
  // - Not installed (isInApp=false): show DownloadApp (download)
  // - Installed & Update available: show DownloadApp (update)
  // - Installed & No update: show the normal app
  if (isMobile) {
    if (!isInApp) {
      return <DownloadApp isInApp={false} updateAvailable={false} />;
    }
    if (updateAvailable) {
      return <DownloadApp isInApp updateAvailable latest={latestMeta} />;
    }
    return children;
  }

  // Desktop → normal app
  return children;
}
